import React, { useId } from 'react';
import styled from 'styled-components';
import { isBackgroundTooDark } from '@dutchie/common/utils/background-contrast';
import { MobileAppFlair } from 'types/graphql';
import {
  CheckerboardPatternDefs,
  FLAIR_VIEWBOX,
  getCheckerboardPatternId,
  getFlairFill,
  getFlairPath,
} from './preview/home-page/get-flair-path';

type TitleFlairPreviewProps = {
  flairType: MobileAppFlair;
  color: string;
};

/** Renders a preview of a mobile app page title on its filled background color, with the
 *  selected decoration along the bottom edge -- mirrors how Background Decoration renders
 *  along the bottom of a colored homepage section. "None" shows no fill at all, matching how
 *  FlairWrapper skips the colored background entirely when there's no decoration to draw. */
export function TitleFlairPreview({ flairType, color }: TitleFlairPreviewProps): JSX.Element {
  const instanceId = useId();
  const path = getFlairPath(flairType);
  const hasFlair = flairType !== MobileAppFlair.none && Boolean(path);
  const patternId = getCheckerboardPatternId(instanceId, color);

  return (
    <Container aria-hidden='true'>
      <ColorBlock $color={hasFlair ? color : ''} $hasFlair={hasFlair}>
        <TitleText $backgroundColor={color} $hasFlair={hasFlair}>
          Flower
        </TitleText>
      </ColorBlock>
      {hasFlair && (
        <FlairEdge $rotated={flairType === MobileAppFlair.angled}>
          <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
            <CheckerboardPatternDefs color={color} patternId={patternId} />
            <path d={path} fill={getFlairFill(flairType, color, patternId)} />
          </svg>
        </FlairEdge>
      )}
      <ScreenBackground />
    </Container>
  );
}

const Container = styled.div`
  background: ${({ theme }) => theme.colors.grey[95]};
  border-radius: 8px;
  margin-top: 8px;
  overflow: hidden;
  width: 100%;
  max-width: 300px;
`;

const ColorBlock = styled.div<{ $color: string; $hasFlair: boolean }>`
  background: ${({ $color }) => $color || 'transparent'};
  padding: ${({ $hasFlair }) => ($hasFlair ? '20px 16px 4px' : '20px 16px')};
`;

// Without a decoration the title sits on the plain preview background rather than the color
// fill, so contrast has to be measured against that -- a muted grey reads on it for any
// configured color, where AA-against-the-fill can pick white and disappear.
const TitleText = styled.p<{ $backgroundColor: string; $hasFlair: boolean }>`
  color: ${({ $backgroundColor, $hasFlair }) => {
    if (!$hasFlair) {
      return '#4f5d68';
    }

    return isBackgroundTooDark($backgroundColor) ? '#ffffff' : '#000000';
  }};
  font-size: 20px;
  font-weight: 700;
  margin: 0;
`;

// Flipped vertically (and horizontally too for the angled shape, matching FlairWrapper's
// $rotated) so the same boundary path used above a section reads correctly below one instead.
const FlairEdge = styled.div<{ $rotated?: boolean }>`
  height: 20px;
  line-height: 0;
  transform: ${({ $rotated }) => ($rotated ? 'scaleX(-1) scaleY(-1)' : 'scaleY(-1)')};
  width: 100%;

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;

// Empty space below the title/decoration so the preview reads more like a screen -- most of the
// page is its plain background, with the title and its decoration confined to the top.
const ScreenBackground = styled.div`
  height: 64px;
`;
